/**
 * Binding a workbook's columns, for a file the engine could not read.
 *
 * **This is ADR-0026 condition 3**: the engine failed, so a person looks at the
 * grid and says which column is which. Everything else in the feature is a
 * guard around this moment.
 *
 * The dialog shows the sheet as it actually is — the first rows, verbatim —
 * because a reviewer must bind what they can SEE rather than confirm what we
 * guessed. Our guess is pre-selected and every one of them is changeable.
 */
import { Alert, Button, Card } from '@fiveox/ui'
import { useCallback, useEffect, useState } from 'react'

import {
  ApiError,
  type InspectBody,
  type MappingProfileRecord,
  type SheetGrid,
  profiles,
} from '../lib/api'

/** Readable names for the fields a profile may bind. */
const FIELD_LABELS: Record<string, string> = {
  invoice_no: 'Invoice no',
  invoice_date: 'Invoice date',
  buyer_name: 'Buyer',
  gstin_buyer: 'Buyer GSTIN',
  supplier_name: 'Supplier',
  gstin_supplier: 'Supplier GSTIN',
  item: 'Item',
  hsn: 'HSN',
  qty: 'Qty',
  uqc: 'UQC',
  gst_rate: 'GST rate',
  taxable: 'Taxable',
  cgst: 'CGST',
  sgst: 'SGST',
  igst: 'IGST',
  cess: 'Cess',
}

export function MappingDialog({
  documentId,
  clientId,
  filename,
  onClose,
  onApplied,
}: {
  documentId: string
  clientId: string
  filename: string
  onClose: () => void
  onApplied: () => void
}) {
  const [body, setBody] = useState<InspectBody | null>(null)
  const [saved, setSaved] = useState<MappingProfileRecord[]>([])
  const [sheet, setSheet] = useState<string>('')
  const [columns, setColumns] = useState<Record<string, string>>({})
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  useEffect(() => {
    void (async () => {
      try {
        const [inspected, existing] = await Promise.all([
          profiles.inspect(documentId),
          profiles.list(clientId),
        ])
        setBody(inspected.data)
        setSaved(existing.data.profiles)
        const first = inspected.data.sheets[0]
        if (first) {
          setSheet(first.sheet)
          setColumns(first.suggested)
          setName(`${first.sheet} layout`)
        }
      } catch (err) {
        setError(
          err instanceof ApiError ? err.message : 'That file could not be inspected.',
        )
      }
    })()
  }, [documentId, clientId])

  const grid: SheetGrid | undefined = body?.sheets.find((s) => s.sheet === sheet)

  const pick = useCallback((sheetName: string) => {
    setSheet(sheetName)
    setColumns(body?.sheets.find((s) => s.sheet === sheetName)?.suggested ?? {})
  }, [body])

  const act = useCallback(
    async (label: string, fn: () => Promise<unknown>) => {
      setBusy(label)
      setError(null)
      try {
        await fn()
      } catch (err) {
        // A refusal here is usually the misfit guard doing its job, and its
        // message names the column. Surfacing a generic one would throw away
        // the only thing that tells the reviewer what to change.
        setError(err instanceof ApiError ? err.message : 'That did not work.')
        throw err
      } finally {
        setBusy(null)
      }
    },
    [],
  )

  if (!body) {
    return (
      <Card className="mb-4 p-4">
        {error ? <Alert tone="error">{error}</Alert> : <p className="gst-meta">Reading the file…</p>}
        <div className="mt-3">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </Card>
    )
  }

  const headerRow = grid?.header_row ?? null

  return (
    <Card className="mb-4 p-4">
      <p className="gst-panel-h mb-1">Bind the columns — {filename}</p>
      <p className="gst-meta mb-3">
        The engine could not read this file, so it needs a person to say which
        column is which{body.direction === 'purchase' ? ' — read as a purchase register, so the counterparty is the supplier' : ''}. What we guessed is selected below; change anything that
        is wrong. The binding is saved for this client and re-used on later
        files of the same shape.
      </p>

      {error && (
        <Alert tone="error" className="mb-3">
          {error}
        </Alert>
      )}

      {saved.length > 0 && (
        <div className="mb-4">
          <p className="gst-meta mb-2">Bindings already saved for this client:</p>
          <div className="flex flex-wrap gap-2">
            {saved.map((p) => (
              <Button
                key={p.id}
                type="button"
                variant="outline"
                size="sm"
                disabled={busy !== null}
                onClick={() =>
                  void act('apply', async () => {
                    await profiles.apply(documentId, p.id)
                    onApplied()
                  })
                }
              >
                Use &ldquo;{p.name}&rdquo;
              </Button>
            ))}
          </div>
        </div>
      )}

      {body.sheets.length > 1 && (
        <div className="mb-3 flex flex-wrap gap-2">
          {body.sheets.map((s) => (
            <Button
              key={s.sheet}
              type="button"
              variant={s.sheet === sheet ? undefined : 'outline'}
              size="sm"
              onClick={() => pick(s.sheet)}
            >
              {s.sheet}
            </Button>
          ))}
        </div>
      )}

      {headerRow === null ? (
        <Alert tone="warning" className="mb-3">
          No header row was recognised on this sheet. Nothing can be bound
          against it, so this file needs a different sheet or a re-export.
        </Alert>
      ) : (
        <>
          {/* The sheet as it is. Wide registers scroll inside their own box
              rather than pushing the page sideways. */}
          <div className="mb-4 overflow-x-auto">
            <table className="gst-table">
              <tbody>
                {(grid?.rows ?? []).slice(0, 8).map((row, r) => (
                  <tr key={`r${r}`} className={r === headerRow ? 'is-strong' : undefined}>
                    {row.map((cell, c) => (
                      <td key={`c${c}`} className="is-text">
                        <span className="gst-mono">{cell}</span>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid gap-2 sm:grid-cols-2">
            {body.bindable.map((fieldName) => (
              <label key={fieldName} className="flex items-center gap-2">
                <span className="gst-meta w-24">
                  {FIELD_LABELS[fieldName] ?? fieldName}
                </span>
                <select
                  className="gst-input flex-1"
                  value={columns[fieldName] ?? ''}
                  onChange={(e) =>
                    setColumns((current) => {
                      const next = { ...current }
                      if (e.target.value === '') delete next[fieldName]
                      else next[fieldName] = e.target.value
                      return next
                    })
                  }
                >
                  <option value="">— not in this file —</option>
                  {(grid?.rows[headerRow] ?? []).map((cell, index) => (
                    <option
                      key={`o${index}`}
                      value={cell.trim() ? `name:${cell}` : `col:${index}`}
                    >
                      {cell.trim() ? cell : `(column ${index + 1}, no header)`}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <input
              className="gst-input w-56"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Name this layout"
            />
            <Button
              type="button"
              size="sm"
              disabled={busy !== null || !name.trim() || Object.keys(columns).length === 0}
              onClick={() =>
                void act('save', async () => {
                  const created = await profiles.save(clientId, {
                    name: name.trim(),
                    sheet,
                    header_row: headerRow,
                    columns,
                    // Served with the grid: a purchase layout saved as a sales
                    // register would hold as a direction conflict on apply.
                    doc_type: body.doc_type,
                  })
                  await profiles.apply(documentId, created.data.id)
                  onApplied()
                })
              }
            >
              {busy === 'save' ? 'Reading…' : 'Save and read this file'}
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </>
      )}
    </Card>
  )
}
